/**
 * 広告ネットワーク定義レジストリ
 *
 * 新しいネットワークを追加する場合はここにエントリを追加するだけでよい。
 * varuna DB / API / UI への反映はすべて自動的に行われる。
 *
 * 各フィールド:
 *   id          - DB・Cookie のキーになる一意な識別子（英小文字）
 *   label       - マイページの設定項目に表示する名称
 *   description - トグル下に表示する説明文
 *   envKey      - PixelID を格納する環境変数名（VUE_APP_XXX）
 *   fire()      - 同意後に呼び出すタグ発火関数
 *   fireLead()  - CV（Lead）イベント発火関数
 */

/**
 * タグ発火のマスタースイッチ。
 *
 * 同意状態の蓄積のみ行い、実際のタグ発火はさせないフェーズでは false のままにする。
 * 発火を有効化する場合のみ、環境変数 VUE_APP_AD_FIRING_ENABLED=true を設定する。
 *
 * ※ ネットワーク個別の Pixel ID 設定（envKey）とは独立した、
 *   「発火してよいか」を明示的に制御する安全ゲート。ID の設定漏れに依存しない。
 */
export const AD_FIRING_ENABLED =
  process.env.VUE_APP_AD_FIRING_ENABLED === 'true'

/**
 * DB記録のマスタースイッチ（発火とは独立）。
 * true にすると「どのタグを発火対象として記録したか」を DB(ad_tags_state)・Cookie に保存する。
 * ステージングで「発火OFFのまま、DB保存だけ動くか」を確認するために分離している。
 */
export const AD_RECORDING_ENABLED =
  process.env.VUE_APP_AD_RECORDING_ENABLED === 'true'

/**
 * 無効化するタグID（カンマ区切り env）。ここに含まれるタグは発火・記録しない。
 * 例: VUE_APP_DISABLED_TAGS=im_dmp  → IMだけ止める
 */
const DISABLED_TAGS = (process.env.VUE_APP_DISABLED_TAGS || '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean)
export function isTagEnabled(tagId) {
  return !DISABLED_TAGS.includes(tagId)
}

/** Meta ピクセルを読み込み PageView を送信 */
function loadMetaPixel() {
  const pixelId = process.env.VUE_APP_META_PIXEL_ID
  if (!pixelId) return
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'PageView')
    return
  }
  ;(function (f, b, e, v, n, t, s) {
    if (f.fbq) return
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = true
    t.src = v
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  )
  window.fbq('init', pixelId)
  window.fbq('track', 'PageView')
}

/** Meta の Lead(CV) イベント */
function fireMetaLead() {
  if (typeof window.fbq === 'function') window.fbq('track', 'Lead')
}

/**
 * IM（Intimate Merger）DMPタグを読み込む。
 * 独立トグルは持たず「Meta同意」に統一して発火させるため、meta の fire() から呼ぶ。
 * - 同意者のみ・発火フラグ(AD_FIRING_ENABLED)ON時のみ実行（呼び出し元 fireNetwork でガード済み）
 * - 発火を呼ぶUI（同意モーダル / マイページ設定）はブラウザ専用のため、アプリWebViewでは実行されない
 * 二重ロード防止のため、一度だけ挿入する。
 */
function loadImTag() {
  if (window.__imTagLoaded) return
  window.__imTagLoaded = true
  ;(function (w, d, s) {
    const f = d.getElementsByTagName(s)[0]
    const j = d.createElement(s)
    j.async = true
    j.src = 'https://dmp.im-apps.net/js/1025721/0001/itm.js'
    f.parentNode.insertBefore(j, f)
  })(window, document, 'script')
}

// ── 同意グループ（ユーザーが同意する単位。モーダル/マイページのトグル） ──
// 1つの同意に複数タグを紐づけられる（例: Meta同意 → Metaピクセル＋IM）。
export const AD_NETWORKS = [
  {
    id: 'meta',
    label: 'Meta（Facebook）広告',
    description: 'Meta広告の最適化に利用状況を使用',
    envKey: 'VUE_APP_META_PIXEL_ID'
  }
  // 追加例: { id: 'tiktok', label: 'TikTok広告', description: '...', envKey: 'VUE_APP_TIKTOK_PIXEL_ID' },
]

/**
 * 実際に発火する個々のタグ。
 *   id        - タグ識別子（ad_tags_state / Cookie のキー）
 *   label     - 管理用の表示名
 *   consentId - どの同意グループ(AD_NETWORKS.id)で発火するか
 *   fire()    - 発火関数
 *   fireLead()- 任意。CV(Lead)イベント
 * タグを追加したい時はこの配列に1行足すだけ。
 */
export const AD_TAGS = [
  {
    id: 'meta_pixel',
    label: 'Meta ピクセル',
    consentId: 'meta',
    fire: loadMetaPixel,
    fireLead: fireMetaLead
  },
  {
    id: 'im_dmp',
    label: 'Intimate Merger DMP',
    consentId: 'meta',
    fire: loadImTag,
    /**
     * 確認画面（/format/rirekisho_job_change/edit/confirm）専用の追加発火。
     * DOM 描画完了後（Vue.nextTick 後）に呼ぶこと。
     * payload は extractConfirmPageEmployments() の戻り値。
     */
    fireConfirmPage(payload) {
      if (!payload) return
      // IM DMP が提供するカスタム変数 API へ職歴データを渡す。
      // itm.js ロード完了後に window.__imTagParams へセットする仕様に従う。
      window.__imTagParams = window.__imTagParams || {}
      window.__imTagParams.employment = payload
    }
  }
]
